import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";

import Button from "@/components/common/Button";
import FormField from "@/components/common/FormField";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { formatRand, formatStatus, GetFilterUrl, showToastNotification } from "@/utility/helper";
import { errorTypes } from "@/utility/validation";
import { IAddDiscountForm, ISingleDiscountResponse, PromoTag } from "@/interfaces/Discount/discount.interface";
import { IProduct, IProductResponse } from "@/interfaces/Product/product.interface";
import { ICategory, ICategoryResponse } from "@/interfaces/Category/category.interface";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import { useFilter } from "@/hooks/useFilter";
import { IFilter } from "@/interfaces/common.interface";
import moment from "moment";
import { usePermissions } from "@/hooks/usePermissions";
import { RootState, useAppSelector } from "@/redux/store";

export const AddDiscount = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id);
    const { isAdmin } = usePermissions();
    const [products, setProducts] = useState<IProduct[]>([]);
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
    const [loadingDiscount, setLoadingDiscount] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<IAddDiscountForm>({
        defaultValues: {
            targetType: '',
            discountType: '',
            targetId: '',
            startDate: '',
            endDate: '',
            campaignType: '',
        }
    });

    const targetType = watch('targetType');
    const discountType = watch('discountType');
    const campaignType = watch('campaignType');
    const value = watch('value');
    const { user } = useAppSelector((state: RootState) => state.user);
    const { filters, updateFilter } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 100, // Fetch more for selection
    });

    useEffect(() => {
        const fetchItems = async () => {
            setLoadingItems(true);
            try {
                if (targetType === 'product') {
                    const perams = GetFilterUrl({ isGlobal: isAdmin ? "" : "0", search: filters.search, ...(isAdmin ? {} : { userId: user?.id }), })
                    const response = await callApi<IProductResponse>(
                        `${API_URL + API_ENDPOINT.products}?limit=100&${perams}`,
                        { method: 'GET' }
                    );
                    setProducts(response?.data?.products || []);
                } else if (targetType === 'category') {
                    const searchParam = filters.search ? `&search=${filters.search}` : '';
                    const response = await callApi<ICategoryResponse>(
                        `${API_URL + API_ENDPOINT.categories}?limit=100${searchParam}`,
                        { method: 'GET' }
                    );
                    setCategories(response?.data?.categories || []);
                }
            } catch (error) {
                console.error("Error fetching items:", error);
            } finally {
                setLoadingItems(false);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            fetchItems();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [targetType, filters.search, isAdmin]);

    useEffect(() => {
        if (isEditMode && id) {
            const fetchDiscountDetails = async () => {
                setLoadingDiscount(true);
                try {
                    const response = await callApi<ISingleDiscountResponse>(
                        `${API_URL + API_ENDPOINT.discounts}/${id}`,
                        { method: 'GET' }
                    );
                    const discount = response?.data;

                    // Extract date part from ISO string
                    const startDate = discount.startDate ? discount.startDate.split('T')[0] : '';
                    const endDate = discount.endDate ? discount.endDate.split('T')[0] : '';

                    const targetId = discount.targetType === 'product' ? discount.productId : discount.categoryId;

                    reset({
                        campaignType: discount.promoTag,
                        name: discount.name,
                        value: Number(discount.value),
                        targetType: discount.targetType,
                        discountType: discount.discountType,
                        startDate: startDate,
                        endDate: endDate,
                        targetId: targetId || ''
                    });
                    if (targetId) {
                        setSelectedTargetIds([targetId]);
                    }
                } catch (error) {
                    ThrowError(error);
                } finally {
                    setLoadingDiscount(false);
                }
            };
            fetchDiscountDetails();
        }
    }, [id, isEditMode, reset]);

    const filteredItems = useMemo(() => {
        if (targetType === 'product') {
            return products;
        } else {
            return categories;
        }
    }, [products, categories, targetType]);

    const handleSelectItem = (item: any, checked: boolean) => {
        if (targetType === 'product') {
            setSelectedTargetIds(prev => {
                const newArr = checked ? [...prev, item.id] : prev.filter(id => id !== item.id);
                setValue('targetId', newArr.length > 0 ? 'selected' : '');
                return newArr;
            });
        } else {
            if (checked) {
                setSelectedTargetIds([item.id]);
                setValue('targetId', item.id);
            } else {
                setSelectedTargetIds([]);
                setValue('targetId', '');
            }
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (targetType === 'product') {
            if (checked) {
                const allIds = filteredItems.map(item => item.id);
                setSelectedTargetIds(allIds);
                setValue('targetId', allIds.length > 0 ? 'selected' : '');
            } else {
                setSelectedTargetIds([]);
                setValue('targetId', '');
            }
        }
    };

    const productColumns: TableColumn<IProduct>[] = [
        {
            key: "product",
            header: "Product",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary/20 overflow-hidden flex-shrink-0 border">
                        {item.media?.[0] ? (
                            <img src={`${API_URL}/${item.media[0].url}`} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground">No Img</div>
                        )}
                    </div>
                    <span className="font-medium text-xs line-clamp-1">{item.name}</span>
                </div>
            )
        },
        {
            key: "stock",
            header: "Stock",
            render: (item) => <span className="text-xs">{item.productInfo?.stockQuantity || 0}</span>
        },
        {
            key: "price",
            header: "Price",
            render: (item) => (
                <div className="font-semibold text-primary">
                    {formatRand(item.price)}
                </div>
            )
        },
    ];

    const categoryColumns: TableColumn<ICategory>[] = [
        {
            key: "category",
            header: "Category",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary/20 overflow-hidden flex-shrink-0 border">
                        {item.media?.url ? (
                            <img src={`${API_URL}/${item.media.url}`} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground">No Img</div>
                        )}
                    </div>
                    <span className="font-medium text-xs line-clamp-1">{item.name}</span>
                </div>
            )
        },
        {
            key: "description",
            header: "Description",
            render: (item) => <span className="text-xs text-muted-foreground line-clamp-1">{item.description || "N/A"}</span>
        },
    ];

    const onSubmit = async (data: IAddDiscountForm) => {
        if (!isEditMode && data.targetType !== 'store' && selectedTargetIds.length === 0) {
            showToastNotification('danger', 'Please select at least one targeted item');
            return;
        }

        try {
            let payload: any;

            if (isEditMode) {
                payload = {
                    name: data.name?.trim(),
                    discountType: data.discountType,
                };
            } else {
                const startISO = new Date(`${data.startDate}`).toISOString();
                const endISO = new Date(`${data.endDate}`).toISOString();

                payload = {
                    promoTag: data.campaignType,
                    name: data.name?.trim(),
                    value: Number(data.value),
                    ...(data.targetType == "category" ? { categoryId: selectedTargetIds[0] } : { productIds: selectedTargetIds }),
                    discountType: data.discountType,
                    targetType: data.targetType,
                    startDate: startISO,
                    endDate: endISO
                };
            }

            console.log(payload)

            const url = isEditMode
                ? `${API_URL + API_ENDPOINT.discounts}/${id}`
                : `${API_URL + API_ENDPOINT.discounts}`;

            const method = isEditMode ? 'PATCH' : 'POST';

            const response = await callApi(url, {
                method,
                data: payload
            });

            showToastNotification('success', response?.message || `Discount ${isEditMode ? 'updated' : 'created'} successfully`);
            navigate(-1);
        } catch (error) {
            ThrowError(error);
        }
    };

    if (loadingDiscount) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <MainHeading
                title={isEditMode ? "Edit Discount" : "Add Discount"}
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="bg-card rounded-lg border p-6 shadow-sm mb-6">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {/* ── Step 1: Campaign Type ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Step 1: Select Campaign Type</h3>
                        <Controller
                            name="campaignType"
                            control={control}
                            render={({ field }) => (
                                <FormField
                                    label="Promo Tag"
                                    type="select"
                                    options={[
                                        { label: "Today's Special", value: PromoTag.TODAYS_SPECIAL },
                                        { label: "Weekend Special", value: PromoTag.WEEKEND_SPECIAL },
                                        { label: "Sale", value: PromoTag.SALE },
                                        { label: "Festive Offer", value: PromoTag.FESTIVE_OFFER },
                                        // { label: "Limited Time Deal", value: PromoTag.LIMITED_TIME_DEAL },
                                        { label: "Trending Deal", value: PromoTag.TRENDING_DEAL }
                                    ]}
                                    value={field.value}
                                    onValueChange={(val) => {
                                        field.onChange(val);
                                        // Reset dates when campaign type changes
                                        setValue('startDate', '');
                                        setValue('endDate', '');
                                        if (val === PromoTag.TODAYS_SPECIAL) {
                                            const today = moment().format('YYYY-MM-DD');
                                            setValue('startDate', today);
                                            setValue('endDate', today);
                                        } else if (val === PromoTag.WEEKEND_SPECIAL) {
                                            const today = moment();
                                            // isoWeekday: 1=Mon, 6=Sat, 7=Sun
                                            // If today is Sunday (7), isoWeekday(6) would go backwards. 
                                            // So if today is Sunday, we might want the next weekend.
                                            let saturday = today.clone().isoWeekday(6);
                                            let sunday = today.clone().isoWeekday(7);
                                            if (today.isoWeekday() === 7) {
                                                saturday = today.clone().add(6, 'days').isoWeekday(6);
                                                sunday = today.clone().add(6, 'days').isoWeekday(7);
                                            }
                                            setValue('startDate', saturday.format('YYYY-MM-DD'));
                                            setValue('endDate', sunday.format('YYYY-MM-DD'));
                                        }
                                    }}
                                    disabled={isEditMode}
                                />
                            )}
                        />
                    </div>

                    {/* ── Step 2: Target Scope ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Step 2: Select Target Scope</h3>
                        <Controller
                            name="targetType"
                            control={control}
                            render={({ field }) => (
                                <FormField
                                    label="Target Scope"
                                    type="select"
                                    options={[
                                        { label: 'Product Level', value: 'product' },
                                        { label: 'Category Level', value: 'category' },
                                        // { label: 'Store-wide', value: 'store' }
                                    ]}
                                    value={field.value}
                                    disabled={isEditMode}
                                    onValueChange={(val) => {
                                        field.onChange(val);
                                        setSelectedTargetIds([]);
                                        setValue('targetId', '');
                                    }}
                                />
                            )}
                        />

                        {targetType !== 'store' && (
                            <div className={`space-y-4 mt-4 ${isEditMode ? 'opacity-60 pointer-events-none' : ''}`}>
                                <div className="relative max-w-xl">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder={`Search for a ${targetType}...`}
                                        className="h-9 pl-9 pr-4 rounded-md border bg-background text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary"
                                        value={filters.search}
                                        onChange={(e) => updateFilter({ search: e.target.value })}
                                    />
                                </div>

                                <div className="max-h-[300px] overflow-y-auto border rounded-xl bg-secondary/5 shadow-inner">
                                    <CommonTable
                                        columns={targetType === 'product' ? productColumns : categoryColumns}
                                        data={filteredItems}
                                        loading={loadingItems}
                                        showCheckbox
                                        onSelectItem={handleSelectItem}
                                        onSelectAll={targetType === 'product' ? handleSelectAll : undefined}
                                        isAllSelected={
                                            targetType === 'product' &&
                                            filteredItems.length > 0 &&
                                            filteredItems.every(item => selectedTargetIds.includes(item.id))
                                        }
                                        selectedItems={new Set(selectedTargetIds)}
                                        getItemId={(item) => item.id}
                                        className="border-none shadow-none"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Step 3: Configure Dates & Schedule ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Step 3: Configure Dates & Schedule</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <FormField
                                label="Start Date"
                                name="startDate"
                                type="date"
                                requiredIcon={!isEditMode}
                                error={errors.startDate?.message}
                                disabled={isEditMode || campaignType === PromoTag.TODAYS_SPECIAL || campaignType === PromoTag.WEEKEND_SPECIAL}
                                min={moment().format('YYYY-MM-DD')}
                                {...register('startDate', {
                                    ...(!isEditMode ? errorTypes.required('Start Date') : {}),
                                    validate: (val) => {
                                        if (campaignType === PromoTag.WEEKEND_SPECIAL && val) {
                                            const day = new Date(val).getDay();
                                            if (day !== 0 && day !== 6) return "Weekend Special must start on a Saturday or Sunday";
                                        }
                                        return true;
                                    }
                                })}
                            />
                            <FormField
                                label="End Date"
                                name="endDate"
                                type="date"
                                requiredIcon={!isEditMode}
                                error={errors.endDate?.message}
                                min={watch('startDate') || moment().format('YYYY-MM-DD')}
                                disabled={isEditMode || campaignType === PromoTag.TODAYS_SPECIAL || campaignType === PromoTag.WEEKEND_SPECIAL}
                                {...register('endDate', {
                                    ...(!isEditMode ? errorTypes.required('End Date') : {}),
                                    validate: (val) => {
                                        if (campaignType === PromoTag.WEEKEND_SPECIAL && val) {
                                            const day = new Date(val).getDay();
                                            if (day !== 0 && day !== 6) return "Weekend Special must end on a Saturday or Sunday";
                                        }
                                        return true;
                                    }
                                })}
                            />
                        </div>
                    </div>

                    {/* ── Step 4: Value and Type ── */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-primary border-b pb-2">Step 4: Value and Type</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 items-start">
                            <FormField
                                label="Discount Name"
                                name="name"
                                placeholder={`${formatStatus(campaignType)} discount`}
                                requiredIcon
                                error={errors.name?.message}
                                {...register('name', { ...errorTypes.required('Name') })}
                            />
                            <Controller
                                name="discountType"
                                control={control}
                                render={({ field }) => (
                                    <FormField
                                        label="Discount Type"
                                        type="select"
                                        options={[
                                            { label: 'Percentage', value: 'percentage' },
                                            { label: 'Flat Amount', value: 'flat' }
                                        ]}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    />
                                )}
                            />
                            <FormField
                                label="Discount Value"
                                name="value"
                                type="number"
                                placeholder={discountType === 'percentage' ? "20" : "150"}
                                requiredIcon
                                min={0}
                                max={discountType === 'percentage' ? 100 : undefined}
                                suffix={discountType === 'percentage' ? <span className="text-muted-foreground text-sm pr-2">%</span> : undefined}
                                prefix={discountType === 'flat' ? <span className="text-muted-foreground text-sm pl-2">R</span> : undefined}
                                error={errors.value?.message}
                                disabled={isEditMode}
                                {...register('value', {
                                    ...(!isEditMode ? errorTypes.required('Discount Value') : {}),
                                    ...(discountType === 'percentage' && !isEditMode ? { max: { value: 100, message: "Percentage cannot exceed 100%" } } : {})
                                })}
                            />

                        </div>
                    </div>

                    {/* ── Actions ── */}
                    <div className="flex justify-end gap-3 pt-8 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            className="px-8 h-11"
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="px-10 h-11 shadow-lg shadow-primary/20"
                            isLoading={isSubmitting}
                            loadingText={isEditMode ? "Updating..." : "Creating..."}
                        >
                            {isEditMode ? "Update Discount" : "Create Discount"}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
